import React, { useCallback, useEffect, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { ShippingMethod } from 'shared/types/cart';
import Button from 'components/commercetools-ui/atoms/button';
import Message from 'components/commercetools-ui/atoms/message';
import Radio from 'components/commercetools-ui/atoms/radio';
import { CurrencyHelpers } from 'helpers/currencyHelpers';
import { useFormat } from 'helpers/hooks/useFormat';
import useLocale from 'helpers/hooks/useLocale';
import useProcessing from 'helpers/hooks/useProcessing';
import { useCart } from 'frontastic';
import CalendarCustomDatePicker from './components/datepicker';

import 'react-datepicker/dist/react-datepicker.css';

export interface Props {
  goToNextStep: () => void;
  isDeliveryError: boolean;
  CTAText?: string;
  retainData?: boolean;
}

const Shipping: React.FC<Props> = ({ goToNextStep, isDeliveryError, CTAText, retainData = false }) => {
  const { formatMessage: formatCartMessage } = useFormat({ name: 'cart' });
  const { formatMessage: formatCheckoutMessage } = useFormat({ name: 'checkout' });
  const [isDateError, setIsDateError] = useState(false);
  const [retainDataCheckDone, setetainDataCheckDone] = useState(false);
  const [loadingShippingMethods, setLoadingShippingMethods] = useState(false);

  const { ctLocale: locale } = useLocale();

  const {
    data,
    setShippingMethod,
    getAvailableShippingMethods,
    hasCustomFitItem,
    hasPersonalisedItem,
    allItemsPersonalized,
  } = useCart();
  const [deliveryKey, setDeliveryKey] = useState<string | null>(null);
  const [deliveryDate, setDeliveryDate] = useState<Date | null>(null);

  const [selectedId, setSelectedId] = useState('');

  const [shippingMethods, setShippingMethods] = useState<ShippingMethod[]>([]);
  const handleDateSelect = (selectedDate: React.SetStateAction<Date | null>) => {
    setDeliveryDate(selectedDate);
  };
  useEffect(() => {
    const fetchAvailableShippingMethods = async () => {
      if (
        data?.shippingAddress &&
        data?.shippingAddress.postalCode !== undefined &&
        data?.shippingAddress.postalCode !== ''
      ) {
        setLoadingShippingMethods(true);
        const availableShippingMethods = await getAvailableShippingMethods();
        setShippingMethods(availableShippingMethods);
        setLoadingShippingMethods(false);
      }
    };

    fetchAvailableShippingMethods();
  }, [data?.shippingAddress?.postalCode, getAvailableShippingMethods]);

  useEffect(() => {
    if (shippingMethods?.[0]) {
      setSelectedId(shippingMethods[0].shippingMethodId);
    }
  }, [shippingMethods]);

  useEffect(() => {
    if (!retainDataCheckDone && data) {
      if (retainData && data?.shippingInfo) {
        setSelectedId(data?.shippingInfo?.shippingMethodId);
      }
      setetainDataCheckDone(true);
    }
  }, [data]);

  // const getEstimatedDate = useCallback((days: number) => {
  //   if (isNaN(days)) return '';

  //   const date = new Date(Date.now());

  //   date.setDate(date.getDate() + days);

  //   return date.toLocaleDateString().replace(/\//g, '-');
  // }, []);

  const { processing, startProcessing, stopProcessing } = useProcessing();

  const submit = useCallback(async () => {
    if (!selectedId || processing) return;

    startProcessing();

    await setShippingMethod(selectedId, deliveryKey || '', deliveryDate || new Date());

    stopProcessing();
    goToNextStep();
  }, [goToNextStep, setShippingMethod, selectedId, processing, startProcessing, stopProcessing, deliveryDate]);

  const handleClick = (deliveryID: string, deliveryKey: string) => {
    setSelectedId(deliveryID);
    setDeliveryKey(deliveryKey);

    if (deliveryKey !== 'choosedelivery') {
      setIsDateError(false);
    }
  };

  return (
    <>
      {data?.custom?.deliveryOption !== 'StoreCollection' && (
        <div className="lg:px-36 lg:pb-36 lg:pt-0">
          {isDeliveryError || shippingMethods.length == 0 ? (
            <div className="mt-25 lg:mt-5">
              <Message
                severity={'error'}
                message={formatCheckoutMessage({
                  id: `shipping.error`,
                  defaultMessage:
                    "Unfortunately, your cart can't be fulfilled to your chosen location . Please change your delivery address to continue.",
                })}
              />
            </div>
          ) : (
            <>
              <div className="mt-24 border-x border-t border-neutral-400 bg-white lg:mt-0">
                {hasCustomFitItem && (
                  <div>
                    <Message
                      severity={'info'}
                      message={formatCheckoutMessage({
                        id: `shipping.customFitItem`,
                        defaultMessage:
                          'If you have only Custom Fit items in your cart, please choose Standard Delivery. If you have Custom Fit and stock items in your cart, please note that stock items will be sent using the delivery method chosen and your Custom Fit order will follow when ready via Standard Delivery.',
                      })}
                    />
                  </div>
                )}
                {hasPersonalisedItem && !allItemsPersonalized && !hasCustomFitItem && (
                  <div>
                    <Message
                      severity={'info'}
                      message={formatCheckoutMessage({
                        id: `shipping.PersonalisedItem`,
                        defaultMessage:
                          'If you have only Custom Fit items in your cart, please choose Standard Delivery. If you have Custom Fit and stock items in your cart, please note that stock items will be sent using the delivery method chosen and your Custom Fit order will follow when ready via Standard Delivery.',
                      })}
                    />
                  </div>
                )}
                {selectedId &&
                  shippingMethods.length > 0 &&
                  shippingMethods?.map((shippingMethod) => (
                    <div
                      key={shippingMethod.shippingMethodId}
                      className="flex cursor-pointer items-center justify-between border-b border-neutral-400 p-16"
                      onClick={() => handleClick(shippingMethod.shippingMethodId, shippingMethod?.key || '')}
                    >
                      <div className="flex items-center gap-16">
                        <Radio
                          name="checkout-shipping-method"
                          checked={shippingMethod.shippingMethodId === selectedId}
                        />
                        <div className="flex flex-col gap-5">
                          <div>
                            <p className="text-14 font-medium">{shippingMethod.name}</p>
                            {/* <p className="mt-4 text-14 text-black">Est: {getEstimatedDate(+(shippingMethod.description ?? 0))}</p> */}
                            <p className="mt-4 text-14 font-light text-black">Est: {shippingMethod.description ?? 0}</p>
                          </div>

                          {deliveryKey === shippingMethod.key && deliveryKey === 'choosedelivery' && (
                            <div>
                              <label htmlFor="delivery-date" className="font-light">
                                {formatCartMessage({ id: 'delivery.date', defaultMessage: 'Date:' })}
                              </label>
                              <div className="mt-4">
                                <CalendarCustomDatePicker
                                  setDeliveryDate={setDeliveryDate}
                                  deliveryDate={deliveryDate}
                                  deliveryKey={deliveryKey}
                                  setIsDateError={setIsDateError}
                                  {...(selectedDate: React.SetStateAction<Date | null>) =>
                                    handleDateSelect(selectedDate)}
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      <span className="text-14 font-medium">
                        {CurrencyHelpers.formatForCurrency(shippingMethod.rates?.[0]?.price ?? {}, locale)}
                      </span>
                    </div>
                  ))}
              </div>
              {loadingShippingMethods && (
                <>
                  <div className="mt-24 border border-neutral-400 bg-white p-16 pl-50 lg:mt-0">
                    <div>
                      <Skeleton className="h-20 w-180"></Skeleton>
                    </div>
                    <div className="mt-7">
                      <Skeleton className="h-20 w-250"></Skeleton>
                    </div>
                  </div>
                </>
              )}
            </>
          )}
          <div className="mt-24">
            <Button
              variant="primary"
              className="w-full min-w-[200px] md:text-14 lg:w-fit lg:px-36"
              disabled={isDeliveryError || shippingMethods.length == 0 || isDateError}
              loading={processing}
              type="submit"
              onClick={submit}
              size="m"
            >
              {CTAText && <span className="text-14 font-bold uppercase">{CTAText}</span>}
              {!CTAText && (
                <>
                  <span className="text-14 font-bold uppercase">
                    {formatCartMessage({ id: 'continue.to', defaultMessage: 'Continue to' })}{' '}
                  </span>
                  <span className="text-14 font-bold uppercase">
                    {formatCartMessage({ id: 'payment', defaultMessage: 'Payment' })}
                  </span>
                </>
              )}
            </Button>
          </div>
        </div>
      )}
    </>
  );
};

export default Shipping;
